/**
 * Delta Buffer
 * Accumulates text chunks and flushes them via a debounced callback.
 */

export interface DeltaBuffer {
  push(text: string): void;
  flush(): void;
  clear(): void;
}

/**
 * Creates a debounced text buffer that accumulates string chunks
 * and flushes them to the provided callback.
 *
 * @param callback - Function to receive the accumulated text on flush
 * @param delayMs - Debounce delay in milliseconds (default: 100)
 */
export function createDeltaBuffer(
  callback: (text: string) => void,
  delayMs = 100
): DeltaBuffer {
  let accumulated = '';
  let timer: ReturnType<typeof setTimeout> | null = null;

  function doFlush(): void {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (accumulated.length > 0) {
      callback(accumulated);
      accumulated = '';
    }
  }

  function scheduleFlush(): void {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      doFlush();
    }, delayMs);
  }

  return {
    push(text: string): void {
      accumulated += text;
      scheduleFlush();
    },

    flush(): void {
      doFlush();
    },

    clear(): void {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      accumulated = '';
    },
  };
}
